import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ContentContainerComponent,
  CurrentBrandScope,
  CurrentScopeFacade,
  EmptyStateComponent,
  TimelineNavigationService,
} from '@principle-theorem/ng-principle-shared';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { Appointment, Practice } from '@principle-theorem/principle-core';
import { type IPractice } from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  multiFilter,
  safeCombineLatest,
  type IReffable,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';
import {
  COULD_NOT_OPEN_DEPOSIT_MESSAGE,
  TakeDepositDialog,
  TakeDepositOpenResult,
} from '../../appointment-view-sidebar/take-deposit-dialog';
import { TakeDepositEntry } from '../../appointment-view-sidebar/take-deposit-entry';
import {
  AppointmentsAwaitingDepositListComponent,
  type IAppointmentAwaitingDeposit,
} from '../appointments-awaiting-deposit-list/appointments-awaiting-deposit-list.component';

@Component({
  selector: 'pr-appointments-awaiting-deposit-section',
  templateUrl: './appointments-awaiting-deposit-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    ContentContainerComponent,
    EmptyStateComponent,
    AppointmentsAwaitingDepositListComponent,
  ],
})
export class AppointmentsAwaitingDepositSectionComponent {
  private _dialog = inject(MatDialog);
  private _timelineNav = inject(TimelineNavigationService);
  private _snackBar = inject(MatSnackBar);
  readonly depositsEnabled$ = inject(CurrentBrandScope).depositsEnabled$;
  appointmentsAwaitingDeposit$: Observable<IAppointmentAwaitingDeposit[]>;

  constructor(currentScopeFacade: CurrentScopeFacade) {
    this.appointmentsAwaitingDeposit$ =
      currentScopeFacade.currentPractice$.pipe(
        filterUndefined(),
        concatMap((practice) => this._awaitingDeposit$(practice))
      );
  }

  async recordDeposit(row: IAppointmentAwaitingDeposit): Promise<void> {
    const openResult = await TakeDepositDialog.open(
      this._dialog,
      row.appointment,
      TakeDepositEntry.Revisit
    );
    if (openResult !== TakeDepositOpenResult.Opened) {
      this._snackBar.open(COULD_NOT_OPEN_DEPOSIT_MESSAGE);
    }
  }

  async viewAppointment(row: IAppointmentAwaitingDeposit): Promise<void> {
    await this._timelineNav.showOnTimeline(row.appointment);
  }

  private _awaitingDeposit$(
    practice: IReffable<IPractice>
  ): Observable<IAppointmentAwaitingDeposit[]> {
    const windowStart = Practice.awaitingDepositWindowStart();
    return Practice.getAppointmentsAwaitingDeposit$(practice, windowStart).pipe(
      multiFilter((appointment) =>
        Practice.isAwaitingDeposit(appointment, windowStart)
      ),
      switchMap((appointments) =>
        safeCombineLatest(
          appointments.map((appointment) =>
            Appointment.patient$(appointment).pipe(
              map((patient) => ({ appointment, patient }))
            )
          )
        )
      )
    );
  }
}
